import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction,
    DoppeActionLayout,
    doppeBusinessEntityDtoIsNotFound,
    doppeDtoProductGetMediaItems,
    DoppeDtoStoreProduct,
    doppeExternalProductProviderResolveBusinessEntityReference,
    doppeListActionGetDefaultListSettings,
    doppeListActionGetDefaultSearchListSettings,
    doppeListActionGetItemClickBehaviourDefaultListSettings,
    doppeListActionItemCtaButtonsResetSettingsForLock,
    doppeListActionListItemSettingsMigrateBaseSettings,
    doppeListActionListItemWithCtaButtonsMigration,
    doppeListActionListItemWithSectionsMigration,
    DoppeListActionPartialSettings,
    DoppeListActionSettings,
    doppeListActionSettingsFilterHiddenListItemsFromActionView,
    doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration,
    doppeListActionTemplateSectionsSettingsDefaultValuesMigration
} from '../../client-server-common';
import {bymoPageContextPropsResolveBusinessEntity} from '../../doppe-sdk/bymo-page/bymo-page-context-props';
import {DoppeActionTypeOutfitViewerLang} from './doppe-action-type-outfit-viewer.lang';
import type {DoppeActionTypeProduct} from '../doppe-action-type-product/doppe-action-type-product';
import type {HideableValue, OverridableValue, RichTextContent} from '@wix/devzai-utils-common';
import {
    arrayFlatten,
    arrayRemoveNullValues,
    asArray,
    hideableValueCreateHidden,
    hideableValueModifyValue,
    immerProduce,
    iterableMapToArray,
    overridableValueCreateOriginal,
    overridableValueGetOverriddenValue,
    overridableValueGetValue,
    overridableValueIsOriginal
} from '@wix/devzai-utils-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';


import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';


export namespace DoppeActionTypeOutfit {


    export interface ProductSettings extends
        DoppeListActionSettings.ListItemWithCtaButtons,
        DoppeListActionSettings.ListItemWithSections,
        DoppeListActionSettings.ListItemBaseSettings,
        DoppeActionTypeProduct.ProductSettings {
        productTitle: OverridableValue<string>;
    }

    export interface ActionSettings extends
        DoppeListActionSettings<ProductSettings>,
        DoppeListActionSettings.WithSearchSettings,
        DoppeListActionSettings.WithItemClickBehaviour,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings
    {
        outfitImage: WixMediaResource | null;
        outfitTitle: HideableValue<string>;
        itemShowPrice: boolean;
        previewShowTitle: boolean;
        previewShowDescription: boolean;
        previewShowPrice: boolean;
        previewShowCoupon: boolean;
    }


    export interface DeprecatedFields extends Partial<DoppeListActionSettings.WithCtaButton> {
        products?: ProductSettings[];
        previewProductButtonText?: string;
    }

    export interface DeprecatedItemFields extends Partial<DoppeActionCtaButtonSettings> {
        productButtonText?: string;
    }

    export type PartialProps = DoppeListActionPartialSettings<ActionSettings, ProductSettings, DeprecatedFields, DeprecatedItemFields>;
}

export const doppeActionTypeOutfit = doppeActionTypeDefineMetadata<DoppeActionTypeOutfit.ActionSettings, DoppeActionTypeOutfit.PartialProps>({
    id: 'io.bymo.action/outfit',
    resolveActionSettings: action => {

        const {
            listDataItems = [],
            ctaButton,
            outfitImage = null,
            outfitTitle = DoppeActionTypeOutfitViewerLang.defaultTexts.outfitTitle,
            itemShowPrice = true,
            previewShowCoupon = true,
            previewShowPrice = true,
            previewShowDescription = true,
            previewShowTitle = true,
            layout = DoppeActionLayout.Grid,
            ...restSettings
        } = action.settings;

        return {
            ...restSettings,
            outfitImage: outfitImage,
            outfitTitle: outfitTitle,
            itemShowPrice: itemShowPrice,
            previewShowCoupon: previewShowCoupon,
            previewShowPrice: previewShowPrice,
            previewShowDescription: previewShowDescription,
            previewShowTitle: previewShowTitle,
            ...doppeListActionGetDefaultSearchListSettings(action.settings),
            ...doppeListActionGetDefaultListSettings<DoppeActionTypeOutfit.ProductSettings>({
                ...action.settings,
                layout: layout,
                listDataItems: listDataItems.map(product => {

                    const {
                        productButtonText,
                        ctaButton,
                        productTitle = '',
                        productPageLink = '',
                        productImage = null,
                        description = hideableValueCreateHidden(''),
                        couponCode = hideableValueCreateHidden(''),
                        productProvider = null,
                        productProviderId = null,
                        ribbon = hideableValueCreateHidden(''),
                        priceData = hideableValueCreateHidden({actualPrice: '', discountPrice: ''}),
                        ...restProductSettings
                    } = product;

                    return {
                        ...restProductSettings,
                        ...doppeListActionListItemSettingsMigrateBaseSettings(product),
                        productTitle: productTitle,
                        productPageLink: productPageLink,
                        productImage: productImage,
                        description: description,
                        couponCode: couponCode,
                        productProvider: productProvider,
                        productProviderId: productProviderId,
                        ribbon: ribbon,
                        ...getDefaultDoppeActionCtaButtonSettings({
                            buttonText: productButtonText,
                            showButton: true
                        }),
                        ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                            hasSourceURL: true,
                            ctaButton: ctaButton,
                        }),
                        ...doppeListActionListItemWithCtaButtonsMigration(product, true),
                        ...doppeListActionListItemWithSectionsMigration(product),
                        priceData: hideableValueModifyValue(priceData, overridableValue => {
                            const innerValue = overridableValueGetValue(overridableValue);
                            const resolved = {...innerValue, discountPrice: innerValue.discountPrice ?? ''};
                            return overridableValueIsOriginal(overridableValue)
                                ? overridableValueCreateOriginal(resolved)
                                : resolved;
                        }),
                    };
                }),
            }),
            ...doppeListActionGetItemClickBehaviourDefaultListSettings(action.settings),
            ...convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction(action.settings),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                ctaButton: ctaButton,
                hasSourceURL: false
            }),
            ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(action.settings, true),
            ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration(action.settings),
        } satisfies DoppeActionTypeOutfit.ActionSettings;
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeListActionItemCtaButtonsResetSettingsForLock(actionSettings);
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            outfitImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            outfitTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            previewShowTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowTitle'}),
            previewShowDescription: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowDescription'}),
            previewShowPrice: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowPrice'}),
            previewShowCoupon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowCoupon'}),
            itemShowPrice: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.itemShowPrice'}),
            itemShowDescription: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.itemShowDescription'}),
            itemShowImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.itemShowImage'}),
            itemShowTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.itemShowTitle'}),
            itemShowTextOnImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.itemShowTextOnImage'}),
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                outfitTitle: true
            },
        bindableContentSettingsNames: {}
    },
    actionListItemSettingsMetadata: {
        contentSettingsMetadata: {
            productTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            productImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            couponCode: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityCouponCode),
            ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon),
            productPageLink: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl),
            priceData: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.PriceData, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                couponCode: true,
                description: true,
                priceData: true,
                ribbon: true
            },
        bindableContentSettingsNames: {
                description: true,
                priceData: true,
                productImage: true,
                productTitle: true,
                ribbon: true
            }
    },
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    isListItemLinkOpener: true,
    resolveUsedMedia: actionSettings => {
        const images = arrayFlatten(actionSettings.listDataItems.map(product => {
            const productImage = overridableValueGetValue(product.productImage);
            return productImage ? asArray(productImage) : []
        }));

        return arrayRemoveNullValues([actionSettings.outfitImage, ...images]);
    },
    resolveMainMedia: (actionSettings, bymoPageContextProps) => {
        const images = arrayFlatten(actionSettings.listDataItems.map(product => {
            const storeProduct = product.productProvider && product.productProviderId ? (() => {
                const entityDto = bymoPageContextPropsResolveBusinessEntity(
                    bymoPageContextProps,
                    doppeExternalProductProviderResolveBusinessEntityReference(product.productProvider!, product.productProviderId!)
                );
                return entityDto && !doppeBusinessEntityDtoIsNotFound(entityDto) ? entityDto as DoppeDtoStoreProduct : null;
            })() : null;

            const productImage = overridableValueGetOverriddenValue(
                product.productImage,
                storeProduct ? doppeDtoProductGetMediaItems(storeProduct) : null
            );
            return productImage ? asArray(productImage) : []
        }));

        return actionSettings.outfitImage ?? images[0] ?? null;
    },
    resolveUsedBusinessEntities: actionSettings => {
        return iterableMapToArray(actionSettings.listDataItems, (product, skip) => {
            return product.productProvider !== null && product.productProviderId !== null ?
                doppeExternalProductProviderResolveBusinessEntityReference(
                    product.productProvider,
                    product.productProviderId
                ) : skip
        });
    },
    resolveListItemsCount: actionSettings => {
        return doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.listDataItems).length;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            if (actionSettings.outfitImage) {
                actionSettings.outfitImage =
                    wixMediaResourceReplaceExternalImage(actionSettings.outfitImage, externalImagesUrlToImageResourceMap)
            }

            for (const item of actionSettings.listDataItems) {
                const productImage = overridableValueGetValue(item.productImage);
                if (productImage) {
                    const images = asArray(productImage);
                    item.productImage = images.map(image => {
                        return wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap)
                    });
                }
            }
        })
    },
    resolveListItems: (action, _viewerLang, _bymoPageProps, _viewerPagesRoutesManager, bymoPageContextProps) => {
        const actionSettings = action.settings;
        return actionSettings.listDataItems.map(item => {
            const storeProduct = item.productProvider && item.productProviderId ? (() => {
                const entityDto = bymoPageContextPropsResolveBusinessEntity(
                    bymoPageContextProps,
                    doppeExternalProductProviderResolveBusinessEntityReference(item.productProvider!, item.productProviderId!)
                );
                return entityDto && !doppeBusinessEntityDtoIsNotFound(entityDto) ? entityDto as DoppeDtoStoreProduct : null;
            })() : null;

            const resolvedProductImage = overridableValueGetOverriddenValue(
                item.productImage,
                storeProduct ? doppeDtoProductGetMediaItems(storeProduct) : null
            );

            return {
                title: overridableValueGetOverriddenValue(item.productTitle, storeProduct?.name ?? ''),
                action: action,
                couponCode: item.couponCode,
                description: hideableValueModifyValue(
                    item.description,
                    value => overridableValueGetOverriddenValue(value, storeProduct?.descriptionPlainText ?? '')
                ) as HideableValue<RichTextContent>,
                image: resolvedProductImage ? asArray(resolvedProductImage)[0] : null,
                itemId: item.id,
                ribbon: hideableValueModifyValue(
                    item.ribbon,
                    value => overridableValueGetOverriddenValue(value, storeProduct?.ribbon ?? '')
                ) as HideableValue<string>,
                url: item.productPageLink,
                enabled: item.enabled,
                deleted: item.deleted,
                displayInSearch: item.displayInSearch,
            }
        });
    },
    resolveListSearchSettings: actionSettings => {
        return actionSettings.listSearchSettings;
    },

});